import React, { useState } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import styled from 'styled-components'
import {
  ModalInjectedProps,
  Modal,
  Button,
  LoadingPlaceholder,
  Flex,
  Box,
  useModalContext,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'
import { SettingsTab } from './SettingsTab'
import { ContentsTab } from './ContentsTab'
import {
  PreviewFormValues,
  PreviewFormProvider,
  usePreviewForm,
} from './usePreviewForm'
import {
  useCreatePreviewMutation,
  GetSharePreviewQuery,
  useDeletePreviewMutation,
  useUpdatePreviewMutation,
  useGetSharePreviewQuery,
} from '@sketch/gql-types'
import { ModalConfirmation } from './ModalConfirmation'

const validationSchema = yup.object({
  previewName: yup.string().required('Preview name is required'),
  description: yup.string(),
  password: yup.string().when('passwordProtected', {
    is: true,
    then: schema =>
      schema
        .required('Password is required when password protection is enabled')
        .min(6, 'Password must be at least 6 characters'),
  }),
  expiryDate: yup.string().when('expiryDateEnabled', {
    is: true,
    then: schema =>
      schema.required('Date is required when preview expiration is enabled'),
  }),
  selectedUUIDs: yup
    .array(
      yup.object({
        uuid: yup.string().required(),
        type: yup.string().oneOf(['PAGE', 'PROTOTYPE']).required(),
      })
    )
    .min(1, 'At least one item must be selected'),
})

// Use the preview type from the GraphQL query
export type PreviewType = NonNullable<GetSharePreviewQuery['share']>['preview']

const formatSelectedUUIDs = (existingPreview?: PreviewType) => {
  const pages =
    existingPreview?.pages.entries
      .filter(item => item.uuid !== null)
      .map(item => ({
        type: 'PAGE' as const,
        uuid: item.uuid!,
      })) || []
  const prototypes =
    existingPreview?.prototypes.entries.map(item => ({
      type: 'PROTOTYPE' as const,
      uuid: item.uuid,
    })) || []

  return [...pages, ...prototypes]
}

const getInitialValues = (
  existingPreview?: PreviewType
): PreviewFormValues => ({
  previewName: existingPreview?.name || '',
  description: existingPreview?.description || '',
  inspectEnabled: existingPreview?.inspectEnabled || false,
  commentsEnabled: existingPreview?.commentsEnabled || false,
  passwordProtected: !!existingPreview?.passwordProtected,
  password: '',
  expiryDate: existingPreview?.expiryDate
    ? new Date(existingPreview.expiryDate).toISOString().split('T')[0]
    : '',
  expiryDateEnabled: !!existingPreview?.expiryDate,
  selectedUUIDs: formatSelectedUUIDs(existingPreview) || [],
})

const ModalStyled = styled(Modal)`
  max-width: 640px;
`

interface PartialShareModalProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  versionShortId: string
  existingPreviewId?: string
}

export const PartialShareModal = ({
  shareName,
  shareIdentifier,
  versionShortId,
  existingPreviewId,
  hideModal,
}: PartialShareModalProps) => {
  const { showToast } = useToast()
  const isEditMode = !!existingPreviewId
  const [previewIdentifier, setPreviewIdentifier] = useState<
    string | undefined
  >(existingPreviewId)

  const { data, loading, error } = useGetSharePreviewQuery({
    variables: { previewIdentifier: previewIdentifier || '', shareIdentifier },
    skip: !previewIdentifier,
  })

  const existingPreview = data?.share?.preview

  const [createPreview] = useCreatePreviewMutation({
    onCompleted: ({ createPreview }) => {
      showToast(
        <>
          The Preview &quot;{createPreview.preview.name}&quot; has been
          successfully created.
        </>
      )

      hideModal()
    },
    onError: 'show-toast',
  })

  const [updatePreview] = useUpdatePreviewMutation({
    onCompleted: ({ updatePreview }) => {
      showToast(
        <>
          The Preview &quot;{updatePreview.preview.name}&quot; has been
          successfully updated.
        </>
      )

      hideModal()
    },
    onError: 'show-toast',
  })

  const [deleteMutation] = useDeletePreviewMutation({
    onCompleted: ({ deletePreview }) => {
      showToast(
        <>
          The Preview &quot;{deletePreview.preview.name}&quot; has been
          successfully deleted.
        </>
      )

      hideModal()
    },
    onError: 'show-toast',
  })

  const handleRemovePreview = () => {
    if (!previewIdentifier) {
      return
    }

    deleteMutation({
      variables: {
        identifier: previewIdentifier,
      },
    })
  }

  const handleSubmit = (values: PreviewFormValues) => {
    const expiryDate =
      values.expiryDateEnabled && !!values.expiryDate
        ? new Date(values.expiryDate).toISOString()
        : undefined

    if (isEditMode && previewIdentifier) {
      updatePreview({
        variables: {
          identifier: previewIdentifier,
          name: values.previewName,
          description: values.description,
          inspectEnabled: values.inspectEnabled,
          commentsEnabled: values.commentsEnabled,
          expiryDate,
          password: values.password.trim(),
          // although we're dealing with UUIDs, the mutation expects them to be under identifier
          content: values.selectedUUIDs.map(selected => ({
            identifier: selected.uuid,
            type: selected.type,
          })),
        },
      })
      return
    }

    return createPreview({
      variables: {
        shareIdentifier,
        name: values.previewName,
        description: values.description,
        inspectEnabled: values.inspectEnabled,
        commentsEnabled: values.commentsEnabled,
        expiryDate,
        password: values.password.trim(),
        // although we're dealing with UUIDs, the mutation expects them to be under identifier
        content: values.selectedUUIDs.map(selected => ({
          identifier: selected.uuid,
          type: selected.type,
        })),
      },
    })
  }

  if (loading) {
    return (
      <ModalStyled onCancel={hideModal}>
        <Flex justifyContent="center" alignItems="center" height={635}>
          <LoadingPlaceholder size="64px" />
        </Flex>
      </ModalStyled>
    )
  }

  if (error) {
    // @TODO: error case
    return (
      <ModalStyled onCancel={hideModal}>
        <Flex
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          height={635}
          p={5}
        >
          <p>An error occurred</p>
          <p>{JSON.stringify(error)}</p>
          <Button onClick={hideModal}>Close</Button>
        </Flex>
      </ModalStyled>
    )
  }

  return (
    <Formik
      initialValues={getInitialValues(existingPreview)}
      validationSchema={validationSchema}
      validateOnMount={true}
      onSubmit={handleSubmit}
    >
      <ModalStyled onCancel={hideModal}>
        <PreviewFormProvider
          previewIdentifier={previewIdentifier}
          setPreviewIdentifier={setPreviewIdentifier}
        >
          <Modal.Body>
            <Modal.Tabs
              steps={[
                {
                  title: 'Settings',
                  content: (
                    <SettingsTab
                      shareName={shareName}
                      isEditMode={isEditMode}
                    />
                  ),
                },
                {
                  title: 'Preview Contents',
                  content: (
                    <ContentsTab
                      shareIdentifier={shareIdentifier}
                      versionShortId={versionShortId}
                    />
                  ),
                },
              ]}
            />
          </Modal.Body>
          <PreviewFormFooter
            onRemovePreview={handleRemovePreview}
            hideModal={hideModal}
            isEditMode={isEditMode}
          />
        </PreviewFormProvider>
      </ModalStyled>
    </Formik>
  )
}

const PreviewFormFooter = ({
  hideModal,
  onRemovePreview,
  isEditMode,
}: {
  hideModal: () => void
  onRemovePreview: () => void
  isEditMode: boolean
}) => {
  const { submitForm, isSubmitting, isValid, dirty } = usePreviewForm()
  const { showModal } = useModalContext()

  const handleRemove = () => {
    showModal(ModalConfirmation, {
      title: 'Remove Preview?',
      description:
        'The link to this preview will no longer work and no-one will be able to access it.',
      confirmText: 'Remove Preview',
      onConfirm: onRemovePreview,
    })
  }

  return (
    <Modal.Footer
      leftContent={
        isEditMode ? (
          <Box ml={4}>
            <Button
              variant="negative-secondary"
              disabled={isSubmitting}
              onClick={handleRemove}
            >
              Remove Preview
            </Button>
          </Box>
        ) : undefined
      }
    >
      <Button disabled={isSubmitting} onClick={hideModal}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={submitForm}
        // submit is disabled until there are changes, when in edit mode
        disabled={isSubmitting || !isValid || (isEditMode && !dirty)}
      >
        {isSubmitting
          ? isEditMode
            ? 'Updating...'
            : 'Creating...'
          : isEditMode
            ? 'Update Preview'
            : 'Create Preview'}
      </Button>
    </Modal.Footer>
  )
}
